import React, { useEffect, useState } from 'react';
import { apiClient } from 'Utils/api';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import * as S from './PageContentRenderer.style';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export interface PageImage {
  id: string;
  originalUrl: string;
  thumbnailUrl: string | null;
  title: Record<string, string>;
  altText: Record<string, string>;
  copyright: Record<string, string>;
}

export interface PageColumn {
  id: string;
  type: 'text' | 'image';
  content: any;
}

export interface PageRow {
  id: string;
  layout: string;
  columns: PageColumn[];
}

interface Props {
  slug: string;
}

const layoutToFlex = (layout: string): string[] => {
  // Returns array of flex-grow values per column
  switch (layout) {
    case '1col':   return ['1'];
    case '2col':   return ['1', '1'];
    case '3col':   return ['1', '1', '1'];
    case '4col':   return ['1', '1', '1', '1'];
    case '1-2':    return ['1', '2'];
    case '2-1':    return ['2', '1'];
    case '1-3':    return ['1', '3'];
    case '3-1':    return ['3', '1'];
    case '1-2-1':  return ['1', '2', '1'];
    case '1-1-2':  return ['1', '1', '2'];
    case '2-1-1':  return ['2', '1', '1'];
    default:       return ['1'];
  }
};

const ImageColumn: React.FC<{ images: PageImage[]; lang: string }> = ({ images, lang }) => {
  const [active, setActive] = useState(0);
  if (!images || images.length === 0) return null;

  if (images.length === 1) {
    const img = images[0];
    return (
      <S.ImageWrapper>
        <S.SingleImage
          src={`${API_URL}${img.originalUrl}`}
          alt={img.altText?.[lang] || img.title?.[lang] || ''}
          title={img.title?.[lang] || ''}
        />
        {img.copyright?.[lang] && <S.Copyright>© {img.copyright[lang]}</S.Copyright>}
      </S.ImageWrapper>
    );
  }

  const current = images[active];
  return (
    <S.SliderWrapper>
      <S.SliderMain>
        <S.SliderArrow
          left
          onClick={() => setActive((a) => (a - 1 + images.length) % images.length)}
          aria-label="Vorheriges Bild"
        >‹</S.SliderArrow>
        <S.SingleImage
          src={`${API_URL}${current.originalUrl}`}
          alt={current.altText?.[lang] || current.title?.[lang] || ''}
          title={current.title?.[lang] || ''}
        />
        <S.SliderArrow
          onClick={() => setActive((a) => (a + 1) % images.length)}
          aria-label="Nächstes Bild"
        >›</S.SliderArrow>
        {current.copyright?.[lang] && <S.Copyright>© {current.copyright[lang]}</S.Copyright>}
      </S.SliderMain>
      <S.SliderDots>
        {images.map((_, i) => (
          <S.SliderDot key={i} $active={i === active} onClick={() => setActive(i)} />
        ))}
      </S.SliderDots>
    </S.SliderWrapper>
  );
};

const TextColumn: React.FC<{ html: string }> = ({ html }) => {
  const safe = DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['img', 'script', 'style', 'iframe', 'object', 'embed'],
  });
  return <S.TextContent dangerouslySetInnerHTML={{ __html: safe }} />;
};

const PageContentRenderer: React.FC<Props> = ({ slug }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'de').slice(0, 2);
  const [rows, setRows] = useState<PageRow[]>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/pages/${slug}`);
        if (!cancel && res.data?.page?.rows) {
          setRows(res.data.page.rows);
        } else if (!cancel) {
          setRows([]);
        }
      } catch (e) {
        if (!cancel) setRows([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  if (rows.length === 0) {
    return (
      <S.Container data-testid={`page-content-${slug}`} />
    );
  }

  return (
    <S.Container data-testid={`page-content-${slug}`}>
      {rows.map((row) => {
        const flexes = layoutToFlex(row.layout);
        const bg = (row as any).background || 'transparent';
        return (
          <S.RowBand key={row.id} background={bg} data-row-bg={bg}>
            <S.RowInner background={bg}>
              <S.Row>
                {row.columns.map((col, i) => (
                  <S.Col key={col.id} flex={flexes[i] || '1'}>
                    {col.type === 'text' && <TextColumn html={col.content?.value?.[lang] || col.content?.value?.de || ''} />}
                    {col.type === 'image' && <ImageColumn images={col.content?.images || []} lang={lang} />}
                  </S.Col>
                ))}
              </S.Row>
            </S.RowInner>
          </S.RowBand>
        );
      })}
    </S.Container>
  );
};

export default PageContentRenderer;
